<template>
  <div class="dropdown" v-if="options">
    <div
      :class="{
        'not-default': !isDefault,
      }"
    >
      <!-- Dropdown Input -->
      <input
        class="dropdown-input"
        ref="inputRef"
        @blur="exit"
        @focus="showOptions"
        @keyup="keyMonitor"
        @keydown.enter="selectFirstResult"
        @keydown.esc="exit"
        :name="name"
        v-model="searchFilter"
        :disabled="disabled"
        :placeholder="placeholder"
        :style="inputStyle"
      />
      <i
        @click="showOptions"
        :class="{
          'fa fa-chevron-down': !optionsShown,
        }"
        style="position: absolute; top: 0.25rem; right: 0.15rem; cursor: pointer"
      ></i>
    </div>

    <!-- Dropdown Menu -->
    <div class="dropdown-content" v-show="optionsShown">
      <div
        :class="{
          'dropdown-item': true,
          active: index === 0,
        }"
        @mousedown="selectOption(option)"
        v-for="(option, index) in filteredOptions"
        :key="index"
      >
        {{ option.name || option.id || '-' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  isDefault: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    required: false,
    default: 'dropdown',
    note: 'Input name',
  },
  options: {
    type: Array,
    required: true,
    default: () => [],
    note: 'Options of dropdown. An array of options with id and name',
  },
  placeholder: {
    type: String,
    required: false,
    default: 'Please select an option',
    note: 'Placeholder of dropdown',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
    note: 'Disable the dropdown',
  },
  maxItem: {
    type: Number,
    required: false,
    default: 6,
    note: 'Max items showing',
  },
  defaultItem: {
    type: Object,
    required: false,
    note: 'the default selected item',
  },
  inputStyle: {
    type: Object,
    required: false,
    note: 'Style of input',
  },
})

const emit = defineEmits(['selected', 'filter'])

// Reactive data
const selected = ref({})
const optionsShown = ref(false)
const searchFilter = ref('')
const inputRef = ref(null)

// Computed properties
const filteredOptions = computed(() => {
  const filtered = []
  const regOption = new RegExp(searchFilter.value, 'ig')

  for (const option of props.options) {
    if (searchFilter.value.length < 1 || option.name.match(regOption)) {
      if (filtered.length < props.maxItem) filtered.push(option)
    }
  }

  if (searchFilter.value !== '' && !isNaN(searchFilter.value)) {
    return sortByInArray(filtered, 'id')
  }

  return filtered
})

// Methods
function sortByInArray(array, key) {
  return array.sort((a, b) => {
    const x = a[key]
    const y = b[key]
    return x < y ? -1 : x > y ? 1 : 0
  })
}

function selectFirstResult() {
  try {
    if (filteredOptions.value[0] && filteredOptions.value[0].id) {
      selected.value = filteredOptions.value[0]
      optionsShown.value = false
      searchFilter.value = selected.value.name
      emit('selected', selected.value)
    } else {
      if (selected.value.id === undefined) {
        selected.value = props.defaultItem
      }
      searchFilter.value = selected.value.name
    }
    optionsShown.value = false
  } catch {
    // Do nothing
  }
}

function selectOption(option) {
  try {
    if (option && option.id != null) {
      selected.value = option
      optionsShown.value = false
      searchFilter.value = selected.value.name
      emit('selected', selected.value)
    } else {
      if (selected.value.id === undefined) {
        selected.value = props.defaultItem
      }
      searchFilter.value = selected.value.name
    }
    optionsShown.value = false
  } catch {
    // do nothing
  }
}

function showOptions() {
  if (!props.disabled) {
    searchFilter.value = ''
    optionsShown.value = true
  }
}

function exit() {
  if (selected.value && selected.value.id === undefined) {
    selected.value = props.defaultItem
  }

  searchFilter.value = selected.value?.name || ''

  if (selected.value && Object.keys(selected.value).length > 0) {
    emit('selected', selected.value)
  }

  optionsShown.value = false
}

function keyMonitor(event) {
  if (event.key === 'Enter' && filteredOptions.value[0]) {
    selectOption(filteredOptions.value[0])
  }
}

// Watchers
watch(searchFilter, (filter) => {
  if (!optionsShown.value) {
    inputRef.value?.blur()
  }

  if (filteredOptions.value.length === 0) {
    selected.value = {}
  }

  emit('filter', filter)
})

// Lifecycle
onMounted(() => {
  if (props.defaultItem && props.defaultItem.id != null && props.defaultItem.name) {
    selected.value = props.defaultItem
    searchFilter.value = props.defaultItem.name
  }
})
</script>

<style scoped>
.dropdown .dropdown-content {
  position: fixed;
  background-color: #fff;
  min-width: 450px;
  max-width: 450px;
  max-height: 248px;
  box-shadow: 0px 8px 8px 0px rgba(0, 0, 0, 0.5);
  overflow: auto;
  overflow-y: scroll;
  z-index: 1;
}

.dropdown {
  position: relative;
  display: block;
  margin: auto;
}

.dropdown-input {
  background: #fff;
  cursor: pointer;
  border: 1px solid #e7ecf5;
  border-radius: 3px;
  color: #333;
  display: block;
  font-size: 0.8em;
  padding: 6px;
  width: 450px;
  text-align: center;
}

.dropdown-input:focus {
  text-align: left;
}

.dropdown-input:hover {
  background: #f8f8fa;
}

.dropdown-input:disabled {
  background: #f8f8fa;
  color: #999;
}

.dropdown-content {
  position: absolute;
  background-color: #fff;
  min-width: 450px;
  max-width: 450px;
  max-height: 248px;
  border: 1px solid #767676;
  overflow: scroll;
  z-index: 1;
}

.dropdown-item {
  color: black;
  font-size: 0.7em;
  line-height: 1em;
  padding: 8px;
  text-decoration: none;
  display: block;
  cursor: pointer;
}

.dropdown-item.active {
  background-color: #e7ecf5;
}

.dropdown-item:hover {
  background-color: #4a6aa0;
  color: white;
}

.dropdown:hover .dropdowncontent {
  display: block;
}

.not-default,
.not-default input {
  position: relative;
  width: 250px !important;
  text-align: left;
}
</style>
